// Keeping full garbage collections out of stage runs (RALLY_GOAL.md §14: no GC pause over 2 ms
// during a run). Measured in Chrome 154 on the Radeon 780M; see DECISIONS.md (GC, M12).
//
// The game itself allocates almost nothing per frame, but the browser does: every WebGPU render pass
// leaves its command encoder, pass encoder, command buffer and descriptor objects in Blink's
// garbage-collected heap (~0.8 KB a pass, ~4–5 KB a frame on Medium). V8 starts a full (V8 + Blink)
// mark-compact once that combined heap has grown ~10% past its size after the last one, which is
// every ~2 minutes of driving here, and a mark-compact of this heap pauses for 2.2–4 ms (more when
// WeakMap ephemerons need extra passes). Two measures keep it off the stage:
//
//  1. Headroom: an ArrayBuffer that is reserved and never touched (address space and commit charge,
//     not RAM) counts toward that combined size, so the 10% becomes ~110 MB instead of ~35 MB:
//     6–7 minutes of driving at 60 Hz. Chromium desktop only (V8's heuristics are what was measured;
//     phones and other engines keep their defaults).
//  2. Checkpoint: before the start lights, the game makes short-lived Blink objects until a
//     mark-compact has run (seen through a FinalizationRegistry on an object that only a full GC can
//     collect), so every stage starts with all of that headroom and the ~3–5 ms pause lands on the
//     start line. Blink objects, not ArrayBuffers: V8's memory reducer would count dead ArrayBuffers
//     as committed memory and schedule its own (memory-reducing, ~13 ms) GCs into the stage.
//
//  3. Room for a long run: after V8's memory reducer has run (below), a full GC sets the next
//     old-generation limit at live + 8 MB + the young generation's capacity (measured: 9.4 MB of
//     headroom with a 1 MB young generation), and V8 starts marking ~4 MB before the limit. The stage
//     promotes ~1 MB a minute into the old generation, so a run longer than ~5 minutes would meet a
//     full GC. Before the checkpoint's full GC the guard therefore grows the young generation to 8 MB
//     (short-lived arrays kept alive across a few scavenges look like a high survival rate): +7 MB of
//     headroom, ~12 minutes of driving. Only for runs that need it: in an uncapped benchmark a big
//     young generation makes each scavenge slower (more garbage per scavenge), and a 70 s run fits
//     in the default headroom.
//
// V8's memory reducer also runs once per page load, 30 s after navigation, as two memory-reducing
// GCs (~13 + 5 ms) when the page is allocating slowly. A player usually meets them in the menu; on
// the first stage start of a page load the checkpoint, and so the lights, wait until they have run
// (`readyAt`): the checkpoint must be the last full GC before the stage. When the page was busy at
// that moment the reducer tries again every 8 s, and until it runs V8's young generation stays at the
// size loading grew it to (32 MB: each scavenge takes ~3 ms). The benchmark's grid hold therefore
// also waits, up to `REDUCER_WAIT_MS` longer, until scavenges come every fraction of a second again
// (`youngReady`), so its measured drive starts as a player's stage does after the menu. Scavenges are
// seen through a FinalizationRegistry on a small unreachable object that the next scavenge collects
// (then registered again); not a WeakRef polled each frame, because deref() keeps its target alive
// until the end of the frame and mid-frame scavenges would promote the probe instead. The real flow's
// lights keep their fixed wait: menu and grid run at the display's rate, the reducer ran before the
// lights in every trace, and a quick start never waits longer than before.

/** Size of the reserved, untouched ArrayBuffer (MB). */
const RESERVE_MB = 768;
/** Extra reserve for presets with six render passes a frame (High, Ultra: GTAO and three shadow
 * cascades) instead of Medium's four: their WebGPU objects fill Blink's share of the headroom at
 * ~0.41 MB/s at 60 Hz against Medium's ~0.28, so 768 MB (~130 MB of headroom) ran out ~5 minutes
 * into a stage. +512 MB gives ~+50 MB: ~7 minutes. Taken at start-up, before V8's memory reducer
 * has run, so the reducer's baseline includes it. */
export const HEAVY_PRESET_EXTRA_MB = 512;
/** While a checkpoint runs, clones per frame of a detached fragment of empty elements: ~5 MB of Blink
 * objects in ~5 ms, and no JS objects (a JS-object burst would grow V8's young generation, and a
 * bigger young generation promotes more during the stage). */
const CLONES_PER_FRAME = 25;
const FRAGMENT_ELEMENTS = 2000;
/** A checkpoint gives up after this many frames (well past any heap limit). */
const MAX_FRAMES = 60;
/** Runs expected to last longer than this (s) get the young-generation stretch (3. above). */
const STRETCH_RUN_S = 240;
/** Stretch: frames of rolling arrays (each frame's batch stays alive for HOLD_FRAMES frames). */
const STRETCH_FRAMES = 12;
const HOLD_ARRAYS_PER_FRAME = 30_000;
const HOLD_FRAMES = 3;
/** V8's memory reducer: due 30 s after navigation (gc_memory_reducer_start_delay_ms); its two GCs are
 * done ~1.5 s later. A start-line checkpoint runs after this (the reducer's memory-reducing GCs leave
 * the old generation a ~9 MB budget, a regular full GC ~40 MB). */
const REDUCER_DUE_MS = 31_500;
/** How much longer than `REDUCER_DUE_MS` the checkpoint waits for the reducer at most. */
const REDUCER_WAIT_MS = 30_000;
/** Scavenges at most this far apart (ms) mean a small young generation (1–2 MB; 32 MB: ~10 s apart). */
const SMALL_YOUNG_GAP_MS = 2000;
/** Probe collections closer together than this (ms) are one GC following another, not a gap. */
const BACK_TO_BACK_MS = 30;

function isChromium(): boolean {
  const ua = (navigator as unknown as { userAgentData?: { brands?: { brand: string }[] } }).userAgentData;
  return !!ua?.brands?.some((b) => /Chromium/.test(b.brand));
}

export class GcGuard {
  /** Reserved headroom (MB), 0 if not reserved. */
  reservedMb: number;
  /** Checkpoints run here (Chromium); elsewhere `begin()` is a no-op. */
  readonly enabled: boolean;
  private reserve: ArrayBuffer | null = null;
  private extra: ArrayBuffer | null = null;
  private sentinel: object | null = { gcGuard: true };
  private token = 0;
  private seen = 0;
  private active = false;
  private frames = 0;
  private fragment: DocumentFragment | null = null;
  private stretchFrames = 0;
  private readonly holds: number[][][] = [];
  private readonly registry: FinalizationRegistry<number> | null;
  /** Scavenge probe: a registered object that the next scavenge collects. */
  private readonly youngProbe: FinalizationRegistry<number> | null;
  private lastScavengeMs = -1;
  /** The last two gaps between scavenges (ms). */
  private gapA = Infinity;
  private gapB = Infinity;
  /** Checkpoints completed and how many saw a full GC (for tests and the benchmark record). */
  checkpoints = 0;
  collected = 0;

  constructor(opts: { reserve: boolean }) {
    this.enabled = typeof FinalizationRegistry !== 'undefined' && typeof document !== 'undefined' && isChromium();
    this.registry = this.enabled ? new FinalizationRegistry<number>((t) => (this.seen = Math.max(this.seen, t))) : null;
    this.youngProbe = this.enabled ? new FinalizationRegistry<number>(() => this.scavenged()) : null;
    this.youngProbe?.register({ probe: true }, 0);
    let mb = 0;
    if (this.enabled && opts.reserve) {
      try {
        this.reserve = new ArrayBuffer(RESERVE_MB * 1024 * 1024);
        mb = RESERVE_MB;
      } catch {
        this.reserve = null;
      }
    }
    this.reservedMb = mb;
  }

  /** More headroom for a heavier preset (`HEAVY_PRESET_EXTRA_MB`); only where the base reserve was taken. */
  topUp(mb: number): void {
    if (!this.reserve || this.extra || mb <= 0) return;
    try {
      this.extra = new ArrayBuffer(mb * 1024 * 1024);
      this.reservedMb += mb;
    } catch {
      this.extra = null;
    }
  }

  /** Reserve headroom only on desktop Chromium with plenty of memory (deviceMemory is capped at 8). */
  static wantsReserve(): boolean {
    const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 0;
    const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
    return mem >= 8 && !coarse;
  }

  /** Start a checkpoint (stage start). */
  /** `runSeconds`: how long the run that follows may last (sizes the old-generation headroom). */
  begin(runSeconds = 0): void {
    if (!this.enabled || this.active) return;
    // The sentinel was made at the end of the previous checkpoint (or at start-up) and has lived
    // through many scavenges since: only a full GC can collect it now.
    if (!this.sentinel) this.sentinel = { gcGuard: true };
    this.registry!.register(this.sentinel, ++this.token);
    this.sentinel = null;
    this.active = true;
    this.frames = 0;
    this.stretchFrames = runSeconds > STRETCH_RUN_S ? STRETCH_FRAMES : 0;
  }

  /** Per frame while a checkpoint runs: allocate a burst, or finish once the full GC was seen. */
  step(): void {
    if (!this.active) return;
    if (this.seen >= this.token || this.frames >= MAX_FRAMES) {
      if (this.seen >= this.token) this.collected++;
      this.checkpoints++;
      this.active = false;
      this.sentinel = { gcGuard: true };
      return;
    }
    if (this.frames < this.stretchFrames) {
      // Young-generation stretch: a batch of small arrays that stays alive for a few frames.
      const batch: number[][] = new Array<number[]>(HOLD_ARRAYS_PER_FRAME);
      for (let i = 0; i < HOLD_ARRAYS_PER_FRAME; i++) batch[i] = [i, i + 0.5, i + 1.5, i + 2.5, i + 3.5, i + 4.5, i + 5.5, i + 6.5];
      this.holds.push(batch);
      if (this.holds.length > HOLD_FRAMES) this.holds.shift();
      if (this.frames === this.stretchFrames - 1) this.holds.length = 0;
      this.frames++;
      return;
    }
    if (!this.fragment) {
      this.fragment = document.createDocumentFragment();
      for (let i = 0; i < FRAGMENT_ELEMENTS; i++) this.fragment.appendChild(document.createElement('i'));
    }
    for (let i = 0; i < CLONES_PER_FRAME; i++) this.fragment.cloneNode(true);
    this.frames++;
  }

  get running(): boolean {
    return this.active;
  }

  /** The probe was collected (a scavenge, or a full GC): note when, and register the next one. */
  private scavenged(): void {
    const now = performance.now();
    // Back-to-back collections (a scavenge just before a full GC) say nothing about the young generation.
    if (this.lastScavengeMs >= 0 && now - this.lastScavengeMs >= BACK_TO_BACK_MS) {
      this.gapA = this.gapB;
      this.gapB = now - this.lastScavengeMs;
    }
    this.lastScavengeMs = now;
    // Only the first checkpoint reads it (`youngReady`): after that, no weak cell for every scavenge.
    if (this.checkpoints === 0) this.youngProbe!.register({ probe: true }, 0);
  }

  /**
   * Whether a stage checkpoint may start now (call after `readyAt`): V8's young generation is small
   * again (the memory reducer has run: the last two scavenges came close together, and recently), or
   * the reducer has been waited for long enough. Only the first checkpoint of a page load waits: the
   * reducer runs once, and after a stage the young generation is 8 MB on purpose (the stretch).
   */
  youngReady(now: number): boolean {
    if (!this.enabled || this.checkpoints > 0 || now >= REDUCER_DUE_MS + REDUCER_WAIT_MS) return true;
    return this.gapA < SMALL_YOUNG_GAP_MS && this.gapB < SMALL_YOUNG_GAP_MS && now - this.lastScavengeMs < SMALL_YOUNG_GAP_MS;
  }

  /** performance.now() after which V8's one-off post-load memory reducer has normally run. */
  get readyAt(): number {
    return this.enabled ? REDUCER_DUE_MS : 0;
  }

  /** Keeps the reserve reachable (and documents that it is deliberate). */
  get reserved(): ArrayBuffer | null {
    return this.extra ?? this.reserve;
  }
}
